import moment from 'moment';

import { SupervisionNoteStatus } from '../../../../constants';
import { SUPERVISION_SETTINGS } from '../constants';
import type { BtSupervisionField } from './types';

import type { ITableHeaderConfig, IBtSupervision } from '../../../../utils/types';

const commonHeaderConfig = [
  {
    label: 'Needs improvement',
    sx: { width: 200, display: 'flex', alignItems: 'center' },
    showRightBorder: true,
  },
  {
    label: 'Meets expectations',
    sx: { width: 200, display: 'flex', alignItems: 'center' },
    showRightBorder: true,
  },
  {
    label: 'Exceeds expectations',
    sx: { width: 200, display: 'flex', alignItems: 'center' },
    showRightBorder: true,
  },
  {
    label: 'Not evaluated',
    sx: { width: 170 },
  },
];

export const skillAcquistionHeaderConfig: Array<ITableHeaderConfig> = [
  {
    label: 'Skill acquisition evaluation',
    sx: { flexGrow: 1 },
    labelAlign: 'left',
    labelWeight: 'h6',
  },
  ...commonHeaderConfig,
];

export const behaviorManagementHeaderConfig: Array<ITableHeaderConfig> = [
  {
    label: 'Behavior management',
    sx: { flexGrow: 1 },
    labelAlign: 'left',
    labelWeight: 'h6',
  },
  ...commonHeaderConfig,
];

export const otherHeaderConfig: Array<ITableHeaderConfig> = [
  {
    label: 'Other',
    sx: { flexGrow: 1 },
    labelAlign: 'left',
    labelWeight: 'h6',
  },
  ...commonHeaderConfig,
];

export const skillAcquisitionData = [
  {
    label: 'Has materials organized prior to session',
    key: 'has_materials_organized_prior_to_session',
    id: 1,
  },
  {
    label: 'Builds rapport with learner',
    key: 'builds_rapport_with_learner',
    id: 2,
  },
  {
    label: 'Implements programs and collects data correctly',
    key: 'implements_programs_and_collects_data_regularly',
    id: 3,
  },
  {
    label: 'Provides clear SDs & keeps appropriate pace',
    key: 'provides_clear_sds_and_keeps_appropriate_pace',
    id: 4,
  },
  {
    label: 'Varies reinforcers/ assesses motivation as needed',
    key: 'varies_reinforces_assesses_as_needed',
    id: 5,
  },
  {
    label: 'Provides praise for independent responses',
    key: 'provides_praise_for_independent_responses',
    id: 6,
  },
  {
    label: 'Uses appropriate ratio of mastered/ novel tasks',
    key: 'uses_appropriate_ratio_of_mastered_novel_tasks',
    id: 7,
  },
  {
    label: 'Uses prompts appropriately',
    key: 'uses_prompts_appropriately',
    id: 8,
  },
  {
    label: 'Implements correction procedures appropriately',
    key: 'implements_correction_procedures_appropriately',
    id: 9,
  },
] satisfies Array<{ label: string; key: BtSupervisionField; id: number }>;

export const behaviorManagementData = [
  {
    label: 'Uses more positive than corrective statements',
    key: 'uses_more_positive_than_corrective_statements',
    id: 1,
  },
  {
    label: 'Uses non-verbal prompts when possible',
    key: 'uses_non_verbal_prompts_when_possible',
    id: 2,
  },
  {
    label: 'Remains calm during challenging behaviors',
    key: 'remains_calm_during_challenging_behaviors',
    id: 3,
  },
  {
    label: 'Follows behavior protocol & records data as outlined',
    key: 'follows_behavior_protocol_and_records_data_as_outlined',
    id: 4,
  },
] satisfies Array<{ label: string; key: BtSupervisionField; id: number }>;

export const otherData = [
  {
    label: 'Professionalism',
    key: 'professionalism',
    id: 1,
  },
  {
    label: 'Collaborates effectively with colleagues',
    key: 'collaborates_effectively_with_colleagues',
    id: 2,
  },
  {
    label: 'Seeks feedback for growth in clinical skills',
    key: 'seeks_feedback_for_growth_in_clinical_skills',
    id: 3,
  },
] satisfies Array<{ label: string; key: BtSupervisionField; id: number }>;

export enum BT_SPVSN_ENUM {
  NEEDS_IMPROVEMENT = 'NEEDS_IMPROVEMENT',
  MEETS_EXPECTATIONS = 'MEETS_EXPECTATIONS',
  EXCEEDS_EXPECTATIONS = 'EXCEEDS_EXPECTATIONS',
  NOT_EVALUATED = 'NOT_EVALUATED',
}

export const BT_SUPERVISION_INITIAL_STATE: IBtSupervision = {
  id: 0,
  client_id: null,
  bt_provider_id: null,
  utc_offset_date_start: 0,
  date_start: null,
  duration: '',
  location: 'HOME',
  setting: SUPERVISION_SETTINGS[0].value,
  date_signed: moment(new Date()),
  utc_offset_date_signed: 0,
  has_materials_organized_prior_to_session: '',
  builds_rapport_with_learner: '',
  implements_programs_and_collects_data_regularly: '',
  provides_clear_sds_and_keeps_appropriate_pace: '',
  varies_reinforces_assesses_as_needed: '',
  provides_praise_for_independent_responses: '',
  uses_appropriate_ratio_of_mastered_novel_tasks: '',
  uses_prompts_appropriately: '',
  implements_correction_procedures_appropriately: '',
  uses_more_positive_than_corrective_statements: '',
  uses_non_verbal_prompts_when_possible: '',
  remains_calm_during_challenging_behaviors: '',
  follows_behavior_protocol_and_records_data_as_outlined: '',
  professionalism: '',
  collaborates_effectively_with_colleagues: '',
  seeks_feedback_for_growth_in_clinical_skills: '',
  additional_comments: '',
  signature: null,
  status: SupervisionNoteStatus.Started,
};

export const BT_SUPERVISION_PLACEHOLDERS = {
  client_id: 'Select Client',
  duration: 'Length of session',
  bt_provider_id: 'Select BT',
};
